import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["onContentLoadStarted"];
import { useCallback, useContext, useEffect, useRef } from 'react';
import host from 'simple-xdm/dist/host';
import { ExperienceTrackerContext } from '@atlassian/experience-tracker';
import { CHANNEL_NAME, EXPERIENCE_FORWARDING_MODULE_NAME, PASSTHROUGH_PROPS_MODULE_NAME, PROPS_UPDATED } from './channel';
function defineModule(name, module) {
  host.defineModule(name, module);
}
var useExperienceForwardingModule = function useExperienceForwardingModule() {
  var experienceTrackerRef = useRef();
  var experienceTracker = useContext(ExperienceTrackerContext);
  if (experienceTrackerRef.current !== experienceTracker) {
    defineModule(EXPERIENCE_FORWARDING_MODULE_NAME, {
      start: function start(options) {
        experienceTracker.start(options);
      },
      abort: function abort(options) {
        experienceTracker.abort(options);
      },
      succeed: function succeed(options) {
        experienceTracker.succeed(options);
      },
      fail: function fail(options) {
        experienceTracker.fail(options);
      }
    });
    experienceTrackerRef.current = experienceTracker;
  }
};
var usePassThroughPropsModule = function usePassThroughPropsModule(props) {
  var onContentLoadStarted = props.onContentLoadStarted,
    passThroughProps = _objectWithoutProperties(props, _excluded);
  var dispatchPropsUpdated = useCallback(function () {
    host.dispatch(PROPS_UPDATED, {
      addon_key: CHANNEL_NAME
    }, {
      message: JSON.stringify(passThroughProps)
    });

    /**
     * Disable react-hooks/exhaustive-deps linting, the dependency has passThroughProps spreading on its keys and values
     * This should be able to handle any changes to the `passThroughProps` would recall the effect function.
     **/
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [].concat(_toConsumableArray(Object.keys(passThroughProps)), _toConsumableArray(Object.values(passThroughProps))));
  var dispatchPropsUpdatedRef = useRef();
  if (dispatchPropsUpdatedRef.current !== dispatchPropsUpdated) {
    var _passThroughProps$nav;
    defineModule(PASSTHROUGH_PROPS_MODULE_NAME, {
      getProps: function getProps() {
        onContentLoadStarted === null || onContentLoadStarted === void 0 || onContentLoadStarted();
        dispatchPropsUpdated();
      },
      navigationPolicyNavigate: (_passThroughProps$nav = passThroughProps.navigationPolicy) === null || _passThroughProps$nav === void 0 ? void 0 : _passThroughProps$nav.navigate,
      onPublish: passThroughProps === null || passThroughProps === void 0 ? void 0 : passThroughProps.onPublish
    });
    dispatchPropsUpdatedRef.current = dispatchPropsUpdated;
  }
  useEffect(dispatchPropsUpdated, [dispatchPropsUpdated]);
};
export function useIframeCommunicationChannel(iframeSrc, passThroughProps) {
  var _channelRef$current;
  var channelRef = useRef();
  useExperienceForwardingModule();
  usePassThroughPropsModule(passThroughProps);
  if (((_channelRef$current = channelRef.current) === null || _channelRef$current === void 0 ? void 0 : _channelRef$current.src) !== iframeSrc) {
    channelRef.current = host.create({
      addon_key: CHANNEL_NAME,
      key: CHANNEL_NAME,
      url: iframeSrc,
      options: {
        globalOptions: {},
        // `hostFrameOffset` - Offset number specified to find iframe's host frame, it will be `window.top` if not specified.
        // Since EP frame can be embedded by middle layer frame(s), we set `hostFrameOffset` to 1 to ensure EP iframe's direct parent is the host frame.
        hostFrameOffset: 1,
        hostOrigin: window.location.origin
      }
    });
  }
  return channelRef.current;
}